"use client"

import { StoreApiData } from "@/(admin)/modules/ui-builder/config/themes/tamda-foods/StoreInfo/type"
import { getStoreById } from "@/services/stores"
import { Stores } from "@/types/codegen/Stores"
import type { StoreListResponse, StoreResponseDataObject } from "@/types/codegen/data-contracts"
import { RequestParams } from "@/types/codegen/http-client"
import { useEffect, useState } from "react"

interface UseStoresOptions {
  pageSize?: number
  locale?: string
  sort?: string
}

export const useStores = (options: UseStoresOptions = {}, localeArg: string = "vi") => {
  const { pageSize = 100, locale: localeOption, sort = "createdAt:desc" } = options
  // locale chấp nhận cả 2 dạng: options.locale (ưu tiên) và tham số vị trí thứ 2 (call site cũ)
  const locale = localeOption || localeArg
  const [stores, setStores] = useState<StoreApiData[]>([])
  const [storeDetail, setStoreDetail] = useState<StoreResponseDataObject | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchStoreDetail = async (storeId: string, locale: string = "vi") => {
    const queryParams = {
      populate: {
        name: true,
        address: true,
        email: true,
        longitude: true,
        latitude: true,
        googleMapCode: true,
        phone: true,
        guidance: true,
        timeRange: true,
        image: true,
        description: true,
        galeryPhotos: true,
        managers: {
          populate: {
            image: true,
          },
        },
      },
      locale,
      sort: "createdAt:desc",
    }
    const res = await getStoreById(Number(storeId), queryParams as RequestParams)
    setStoreDetail(res?.data as StoreResponseDataObject)
  }

  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoading(true)
        setError(null)

        const api = new Stores()
        const res = await api.getStores(
          {
            populate: "*",
            locale,
            sort,
            "pagination[pageSize]": pageSize,
          },
          { baseUrl: undefined },
        )

        const data = (res.data as StoreListResponse) || {}

        if (!data || !Array.isArray(data.data)) {
          throw new Error("Invalid data format received from API")
        }

        const storeApiData: StoreApiData[] = (data.data || []).map(item => {
          const attributes = item.attributes || {}
          const imageData = attributes.image?.data
          const imageUrl = imageData?.attributes?.url || ""

          // Transform timeRange from BlocksTimeRangeComponent[] to TimeRangeItem[]
          const timeRange = (attributes.timeRange || []).map((tr, index) => ({
            id: tr.id ?? index,
            from: tr.from || "",
            to: tr.to || "",
            day: tr.day || "",
          }))

          // Mỗi bản dịch của 1 store là 1 entry Strapi với id riêng. Giữ lại id các bản dịch
          // để khớp được storeId đã lưu trong Puck dù nó thuộc locale khác.
          const localizations = (attributes as { localizations?: { data?: Array<{ id?: number }> } }).localizations
          const localizationIds = (localizations?.data || [])
            .map(loc => loc?.id)
            .filter((locId): locId is number => typeof locId === "number")

          return {
            id: item.id || 0,
            localizationIds,
            attributes: {
              name: attributes.name || "",
              address: attributes.address || "",
              email: attributes.email || "",
              longitude: attributes.longitude || 0,
              latitude: attributes.latitude || 0,
              googleMapCode: attributes.googleMapCode || "",
              phone: attributes.phone || "",
              guidance: attributes.guidance || "",
              description: attributes.description || "",
              image: imageUrl,
              timeRange,
            },
          }
        })

        setStores(storeApiData)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch stores"
        setError(errorMessage)
        console.error("Error fetching stores:", err)
        setStores([])
      } finally {
        setLoading(false)
      }
    }

    fetchStores()
  }, [pageSize, locale, sort])

  return { stores, loading, error, storeDetail, fetchStoreDetail }
}
